import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';

import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';

import { LESSON_IN_WORK, MAIN_DOMAIN } from '@app/app.constants';
import { Lesson, LessonStatus, Pupil, CardInterface } from '@df/interfaces';
import { APIService } from '@app/_services/api.service';

@Injectable({
    providedIn: 'root'
})
export class TeacherService {
    public subjectIdChanged = new Subject<number>();
    public classIdChanged = new Subject<number>();
    public onSelect = new Subject<Pupil>();
    public lessonDate: Date;
    public lessonDateChanged = new Subject<Date>();
    selectedPupil: Pupil;
    activeCard: CardInterface;
    displayedColumns: Array<{ id: number; title: string; active: boolean }>;

    private classId: number;
    private subjectId: number;

    constructor(private http: HttpClient, private datePipe: DatePipe, private api: APIService) {}

    public setDate(date: Date): void {
        this.lessonDate = date;
        this.lessonDateChanged.next(this.lessonDate);
    }

    public getTeacherClasses(): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/people/teacher/classes/`);
    }

    public getClass(class_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/people/class/${class_id}/`);
    }

    public convertDateFromServerTimeToLocal(lesson: Lesson): string {
        return this.datePipe.transform(new Date(lesson.date), 'yyyy-MM-ddTHH:mm');
    }

    public convertDateFromServerTimeToLocalString(lesson: Lesson): string {
        return this.datePipe.transform(new Date(lesson.date), 'dd.MM.yyyyTHH:mm');
    }

    public getTeacherSubjects(): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/subject/`);
    }

    public getLesson(lesson_id: string): Observable<Lesson> {
        return this.http.get<Lesson>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`);
    }

    public getStudentsWithResults(lesson_id): Observable<any> {
        const lessonId = Number(lesson_id);
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${lessonId}/students_with_results/`);
    }

    public createNewLesson(class_id: number, data: any): Observable<Lesson> {
        return this.http.post<Lesson>(`${MAIN_DOMAIN}/api/lessons/lesson/${class_id}/create`, data);
    }

    public checkLessonNumber(class_id: number, lesson_number: number): Observable<any> {
        return this.http.get<Lesson>(`${MAIN_DOMAIN}/api/lessons/lesson/${class_id}/${lesson_number}/check`);
    }

    public getAvailableLessonNumbers(classID: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${classID}/available-numbers`);
    }

    public getLessonsTheme(): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/theme/`);
    }

    public getLessonsPagination(lesson_id: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/lessons/lesson/pagination/${lesson_id}/`);
    }

    public getLessonById(lesson_id: string): Observable<Lesson> {
        return this.http.get<Lesson>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`);
    }

    public getInitialAnalytics(lesson_id: string): Observable<any> {
        const url = `main_analytics/initial-data/${lesson_id}/`;
        return this.api.get(url);
    }

    public getAnalytics(lesson_id: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/main_analytics/common-table/${lesson_id}/`);
    }

    public getIndividualPupilAnalytics(pupil_id: string, lesson_id: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/main_analytics/individual-data/${pupil_id}/${lesson_id}/`);
    }

    public getIndividualLessonGraph(lesson_id: string, pupil_id: string): Observable<any> {
        return this.http.get<any>(
            `${MAIN_DOMAIN}/api/main_analytics/individual-graph-lesson/${lesson_id}/${pupil_id}/`
        );
    }

    public getIndividualGraphPlot(lesson_id: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/main_analytics/individual-graph-plot/${lesson_id}/`);
    }

    public getSummaryTable(lesson_id: string): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/main_analytics/summary-table/${lesson_id}/`);
    }

    public updateLessons(lesson_id: string, data: any): Observable<any> {
        return this.http.patch<any>(
            `${MAIN_DOMAIN}/api/lessons/lesson/status/${lesson_id}/`,
            JSON.parse(JSON.stringify(data))
        );
    }

    public finishLessonWithTimeCorrection(lessonID: number, data): Observable<any> {
        return this.http.post(`${MAIN_DOMAIN}/api/lessons/lesson/${lessonID}/finish`, data);
    }

    // @ts-ignore-start: "lesson_status" используется просто линтер не может увидеть его
    public getClassLessonsByStatus(lesson_status: LessonStatus, class_id: any): Observable<any> {
        // @ts-ignore-end
        return this.http.get<any>(
            `${MAIN_DOMAIN}/api/lessons/lesson/?status=${LESSON_IN_WORK}&student_class=${class_id}`
        );
    }

    public updateLesson(lesson_id: string, data: any): Observable<any> {
        return this.http.patch<any>(
            `${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`,
            JSON.parse(JSON.stringify(data))
        );
    }

    public editLesson(lesson_id: number, data: any): Observable<any> {
        return this.http.patch<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/update`, data);
    }

    public removeLesson(lesson_id: number): Observable<any> {
        return this.http.delete<any>(`${MAIN_DOMAIN}/api/lessons/lesson/${lesson_id}/`);
    }

    public copyTask(task: any): Observable<any> {
        return this.http.post<any>(`${MAIN_DOMAIN}/api/tasks/box/copy/`, task);
    }

    public setClassId(class_id: number): void {
        this.classId = class_id;
        this.classIdChanged.next(this.classId);
    }

    public setSubjectId(subject_id: number): void {
        this.subjectId = subject_id;
        this.subjectIdChanged.next(this.subjectId);
    }

    public choosePupil(pupil): void {
        localStorage.setItem('individualDataSelectedStudent', JSON.stringify(pupil));
        this.selectedPupil = pupil;
        this.onSelect.next(this.selectedPupil);
    }

    public clearPupil(): void {
        this.selectedPupil = null;
        this.onSelect.next();
    }

    set setCurrentStudent(pupil: Pupil) {
        this.selectedPupil = pupil;
    }

    get getCurrentStudent(): Pupil {
        return this.selectedPupil;
    }

    set setDisplayedColumns(columns: Array<{ id: number; title: string; active: boolean }>) {
        this.displayedColumns = columns;
    }
}
